import { Link, router } from '@inertiajs/react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import SurveyLayout from '@/layouts/SurveyLayout'
import PageHeader from '@/components/PageHeader'
import SurveyLinkBuilder from '@/components/SurveyLinkBuilder'
import SurveyQrCode from '@/components/SurveyQrCode'
import { QRCodeSVG } from 'qrcode.react'
import type { SurveyDetail, Question } from '@/types'
import { SURVEY_TYPE_LABELS } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface ShowProps {
  survey: SurveyDetail
}

const STATUS_BADGE: Record<SurveyDetail['status'], string> = {
  draft: 'badge badge-warning',
  active: 'badge badge-success',
  closed: 'badge badge-error',
}

const STATUS_LABEL: Record<SurveyDetail['status'], string> = {
  draft: 'Draft',
  active: 'Active',
  closed: 'Closed',
}

import { SCALE_PRESETS } from '@/types'

function questionTypeLabel(q: Question): string {
  if (q.question_type === 'scale') {
    if (q.scale_labels) return `Custom (1–${q.scale_points})`
    if (q.scale_preset && SCALE_PRESETS[q.scale_preset]) return SCALE_PRESETS[q.scale_preset].label
    return `Scale (1–${q.resolved_points})`
  }
  if (q.question_type === 'open_text') return 'Open Text'
  if (q.question_type === 'single_choice') return 'Single Choice'
  return q.question_type
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Show({ survey }: ShowProps) {
  const deleteModalRef = useRef<HTMLDialogElement>(null)
  const closeModalRef = useRef<HTMLDialogElement>(null)
  const publishedModalRef = useRef<HTMLDialogElement>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const ap = useAccountPath()
  const surveyUrl = `${window.location.origin}/${survey.slug}`
  // Retrospective is per question, so the survey-level answer is a count, not a
  // yes/no — a mixed instrument is the normal case now.
  const retrospectiveCount = survey.questions.filter((q) => q.retrospective).length
  const scaleCount = survey.questions.filter((q) => q.question_type === 'scale').length

  // The publish redirect appends ?published=1 — celebrate once, then strip the
  // param so refresh/back doesn't re-open the modal.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('published') === '1') {
      publishedModalRef.current?.showModal()
      params.delete('published')
      const query = params.toString()
      // Preserve Inertia's history.state.page — replacing it with {} breaks
      // back-navigation restoration.
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : ''))
    }
  }, [])

  function handleCopyLink() {
    navigator.clipboard.writeText(surveyUrl).then(() => {
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    })
  }

  function handlePublish() {
    router.post(ap(`/surveys/${survey.id}/publish`))
  }

  function handleClose() {
    router.post(ap(`/surveys/${survey.id}/close`))
  }

  function handleReopen() {
    router.post(ap(`/surveys/${survey.id}/reopen`))
  }

  function handleDuplicate() {
    router.post(ap(`/surveys/${survey.id}/duplicate`))
  }

  function handleDelete() {
    router.delete(ap(`/surveys/${survey.id}`))
  }

  return (
    <SurveyLayout survey={survey}>
      <div className="space-y-6">
        <PageHeader
          title={survey.name}
          description={survey.programme_name}
          badge={<span className={STATUS_BADGE[survey.status]}>{STATUS_LABEL[survey.status]}</span>}
          actions={<>
            {survey.status !== 'draft' && (
              <Link href={ap(`/surveys/${survey.id}/results`)} className="btn btn-primary btn-sm">
                <span className="iconify lucide--bar-chart-3 size-4" />
                View Results
              </Link>
            )}
            <Link href={ap(`/surveys/${survey.id}/edit`)} className="btn btn-ghost btn-sm">
              <span className="iconify lucide--pencil size-4" />
              Edit
            </Link>
            <button onClick={handleDuplicate} className="btn btn-ghost btn-sm">
              <span className="iconify lucide--copy size-4" />
              Duplicate
            </button>
          </>}
        />

        {survey.demo && (
          <div role="alert" className="alert alert-soft alert-info text-sm">
            <span className="iconify lucide--sparkles size-4" />
            <span>
              This is a sample survey with example data — explore the results, then delete it
              when you're done.
            </span>
          </div>
        )}

        {/* Link builder */}
        <SurveyLinkBuilder survey={survey} baseUrl={surveyUrl} />

        {/* Survey details card */}
        <div className="card bg-base-100">
          <div className="card-body space-y-4">
            <h2 className="text-lg font-semibold">Survey Details</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-base-content/60">Type</dt>
              <dd>{SURVEY_TYPE_LABELS[survey.survey_type] ?? survey.survey_type}</dd>

              <dt className="text-base-content/60">Template</dt>
              <dd>{survey.template_name}</dd>

              <dt className="text-base-content/60">Mode</dt>
              <dd>
                {retrospectiveCount > 0 ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="badge badge-info badge-sm">Retrospective (post-then-pre)</span>
                    <span className="text-base-content/70">
                      {retrospectiveCount} of {scaleCount}{' '}
                      {scaleCount === 1 ? 'scale question' : 'scale questions'}
                    </span>
                  </span>
                ) : (
                  <span className="text-base-content/70">Standard</span>
                )}
              </dd>

              <dt className="text-base-content/60">Responses</dt>
              <dd className="tabular-nums">{survey.response_count}</dd>

              <dt className="text-base-content/60">Created</dt>
              <dd>{formatDate(survey.created_at)}</dd>

              {survey.published_at && (
                <>
                  <dt className="text-base-content/60">Published</dt>
                  <dd>{formatDateTime(survey.published_at)}</dd>
                </>
              )}

              {survey.closed_at && (
                <>
                  <dt className="text-base-content/60">Closed</dt>
                  <dd>{formatDateTime(survey.closed_at)}</dd>
                </>
              )}

              <dt className="text-base-content/60">Branding</dt>
              <dd className="flex items-center gap-2">
                <span
                  className="inline-block size-4 rounded-full border border-base-300"
                  style={{ backgroundColor: survey.primary_colour }}
                />
                <span className="text-base-content/70">{survey.consultancy_name}</span>
              </dd>
            </dl>

            {survey.welcome_message && (
              <div>
                <h3 className="text-sm font-medium text-base-content/60 mb-1">Welcome message</h3>
                <p className="text-sm">{survey.welcome_message}</p>
              </div>
            )}

            {survey.closing_message && (
              <div>
                <h3 className="text-sm font-medium text-base-content/60 mb-1">Closing message</h3>
                <p className="text-sm">{survey.closing_message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Questions card */}
        <div className="card bg-base-100">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                Questions
                <span className="badge badge-sm badge-ghost ms-2">{survey.questions.length}</span>
              </h2>
              {survey.status === 'draft' && (
                <Link
                  href={ap(`/surveys/${survey.id}/edit`)}
                  className="btn btn-ghost btn-sm"
                >
                  <span className="iconify lucide--pencil size-4" />
                  Edit questions
                </Link>
              )}
            </div>

            {survey.questions.length > 0 ? (
              <ol className="space-y-2">
                {survey.questions.map((q, i) => (
                  <li key={q.id} className="flex items-start gap-3 text-sm">
                    <span className="text-base-content/40 tabular-nums shrink-0 mt-0.5 w-5 text-end">
                      {i + 1}.
                    </span>
                    <div className="flex-1 min-w-0">
                      <span>{q.question_text}</span>
                      {q.required && (
                        <span className="text-error ms-1" title="Required">*</span>
                      )}
                    </div>
                    {q.retrospective && (
                      <span className="badge badge-soft badge-info badge-sm shrink-0" title="Asks for a before and an after rating">
                        Before / After
                      </span>
                    )}
                    <span className="badge badge-soft badge-sm shrink-0">
                      {questionTypeLabel(q)}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-base-content/50 text-sm">
                No questions added yet.{' '}
                <Link href={ap(`/surveys/${survey.id}/edit`)} className="link link-hover">
                  Add questions
                </Link>
              </p>
            )}
          </div>
        </div>

        {/* Facilitators card */}
        <FacilitatorsSection survey={survey} />



        {/* Lifecycle actions */}
        <div className="flex items-center justify-between">
          <div>
            {(survey.status === 'draft' || survey.demo) && (
              <button
                onClick={() => deleteModalRef.current?.showModal()}
                className="btn btn-ghost btn-sm text-error"
              >
                <span className="iconify lucide--trash-2 size-4" />
                {survey.demo ? 'Delete sample survey' : 'Delete survey'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {survey.status === 'draft' && (
              <button onClick={handlePublish} className="btn btn-primary btn-sm">
                <span className="iconify lucide--send size-4" />
                Publish survey
              </button>
            )}
            {survey.status === 'active' && !survey.demo && (
              <button onClick={() => closeModalRef.current?.showModal()} className="btn btn-warning btn-sm">
                <span className="iconify lucide--circle-stop size-4" />
                Close survey
              </button>
            )}
            {survey.status === 'closed' && !survey.demo && (
              <button onClick={handleReopen} className="btn btn-success btn-sm">
                <span className="iconify lucide--rotate-ccw size-4" />
                Reopen survey
              </button>
            )}
          </div>
        </div>

        {/* Just-published share modal */}
        <dialog ref={publishedModalRef} className="modal">
          <div className="modal-box max-w-lg">
            <div className="text-center space-y-2 mb-4">
              <span className="iconify lucide--party-popper size-10 text-success" />
              <h3 className="text-lg font-bold">Your survey is live</h3>
              <p className="text-sm text-base-content/60">
                Share this link with participants — responses appear in Results as they arrive.
              </p>
            </div>

            <div className="join w-full">
              <input
                type="text"
                readOnly
                className="input input-sm join-item w-full font-mono text-xs"
                value={surveyUrl}
                onFocus={(e) => e.target.select()}
              />
              <button onClick={handleCopyLink} className="btn btn-primary btn-sm join-item">
                <span className={`iconify ${linkCopied ? 'lucide--check' : 'lucide--copy'} size-4`} />
                {linkCopied ? 'Copied' : 'Copy'}
              </button>
            </div>

            <div className="flex items-center justify-center gap-2 mt-4">
              <SurveyQrCode url={surveyUrl} surveyName={survey.name} />
              <a
                href={surveyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-ghost"
              >
                <span className="iconify lucide--external-link size-4" />
                Preview survey
              </a>
              {survey.anonymity === 'named' && (
                <Link
                  href={ap(`/surveys/${survey.id}/respondents`)}
                  className="btn btn-sm btn-primary"
                >
                  <span className="iconify lucide--users size-4" />
                  Invite respondents
                </Link>
              )}
            </div>

            <div className="modal-action">
              <form method="dialog">
                <button className="btn btn-ghost btn-sm">Done</button>
              </form>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>

        {/* Close survey confirmation modal */}
        <dialog ref={closeModalRef} className="modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold">Close survey</h3>
            <p className="py-4 text-sm">
              Are you sure you want to close <strong>{survey.name}</strong>? Respondents will no longer be able to submit responses. You can reopen it later.
            </p>
            <div className="modal-action">
              <form method="dialog">
                <button className="btn btn-ghost btn-sm">Cancel</button>
              </form>
              <button onClick={handleClose} className="btn btn-warning btn-sm">
                Close survey
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>

        {/* Delete confirmation modal */}
        <dialog ref={deleteModalRef} className="modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold">Delete survey</h3>
            <p className="py-4 text-sm">
              Are you sure you want to delete <strong>{survey.name}</strong>? This action cannot be undone.
            </p>
            <div className="modal-action">
              <form method="dialog">
                <button className="btn btn-ghost btn-sm">Cancel</button>
              </form>
              <button onClick={handleDelete} className="btn btn-error btn-sm">
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      </div>
    </SurveyLayout>
  )
}

/* ----- Facilitators section ----- */

function FacilitatorsSection({ survey }: { survey: SurveyDetail }) {
  const [selectedFacilitator, setSelectedFacilitator] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [sessionQrFacilitator, setSessionQrFacilitator] = useState<typeof survey.survey_facilitators[0] | null>(null)
  const [sessionId, setSessionId] = useState('')
  const sessionDialogRef = useRef<HTMLDialogElement>(null)
  const ap = useAccountPath()

  function handleAssign(e: FormEvent) {
    e.preventDefault()
    if (!selectedFacilitator) return
    router.post(ap(`/surveys/${survey.id}/facilitators`), { facilitator_id: selectedFacilitator }, {
      preserveScroll: true,
      onSuccess: () => setSelectedFacilitator(''),
    })
  }

  function handleCreateAndAssign(e: FormEvent) {
    e.preventDefault()
    if (!newFirstName.trim() || !newEmail.trim()) return
    router.post(ap('/facilitators'), {
      survey_id: survey.id,
      facilitator: {
        first_name: newFirstName.trim(),
        last_name:  newLastName.trim(),
        email_address:      newEmail.trim(),
      },
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setNewFirstName('')
        setNewLastName('')
        setNewEmail('')
        setShowAddForm(false)
      },
    })
  }

  function handleRemove(sfId: number) {
    if (!confirm('Remove this facilitator from the survey?')) return
    router.delete(ap(`/surveys/${survey.id}/facilitators/${sfId}`), { preserveScroll: true })
  }

  function handleCopy(url: string, token: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 2000)
    })
  }

  return (
    <>
      <div className="card bg-base-100">
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Facilitators
              {survey.survey_facilitators.length > 0 && (
                <span className="badge badge-sm badge-ghost ms-2">{survey.survey_facilitators.length}</span>
              )}
            </h2>
          </div>

          {/* Assigned facilitators */}
          {survey.survey_facilitators.length > 0 ? (
            <div className="space-y-3">
              {survey.survey_facilitators.map((sf) => (
                <div key={sf.id} className="flex items-start gap-3 rounded-lg border border-base-300 p-3 text-sm group">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{sf.name}</div>
                    <div className="text-xs text-base-content/60">{sf.email_address}</div>
                    {survey.published_at && (
                      <div className="mt-2">
                        <code className="text-xs bg-base-200 px-1.5 py-0.5 rounded break-all">
                          {sf.survey_url}
                        </code>
                        <div className="flex gap-1 mt-1.5">
                          <button
                            onClick={() => handleCopy(sf.survey_url, sf.token)}
                            className="btn btn-ghost btn-xs"
                          >
                            <span className={`iconify ${copiedToken === sf.token ? 'lucide--check' : 'lucide--clipboard'} size-3`} />
                            {copiedToken === sf.token ? 'Copied' : 'Copy'}
                          </button>
                          <SurveyQrCode url={sf.survey_url} surveyName={`${survey.name} - ${sf.name}`} />
                          <button
                            onClick={() => {
                              setSessionQrFacilitator(sf)
                              setSessionId('')
                              sessionDialogRef.current?.showModal()
                            }}
                            className="btn btn-primary btn-xs"
                          >
                            <span className="iconify lucide--qr-code size-3" />
                            Session QR
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemove(sf.id)}
                    className="btn btn-ghost btn-sm btn-square text-error opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    title="Remove from survey"
                  >
                    <span className="iconify lucide--x size-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-base-content/50">
              No facilitators assigned. Assign facilitators to generate unique survey links for each one.
            </p>
          )}

          {/* Assign existing */}
          {survey.available_facilitators.length > 0 && (
            <form onSubmit={handleAssign} className="flex gap-2 pt-2 border-t border-base-300/50">
              <select
                className="select select-sm flex-1"
                value={selectedFacilitator}
                onChange={(e) => setSelectedFacilitator(e.target.value)}
              >
                <option value="">Assign a facilitator...</option>
                {survey.available_facilitators.map((f) => (
                  <option key={f.id} value={f.id}>{f.name} ({f.email_address})</option>
                ))}
              </select>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!selectedFacilitator}>
                Assign
              </button>
            </form>
          )}

          {/* Create new */}
          <div className="pt-2 border-t border-base-300/50">
            {showAddForm ? (
              <form onSubmit={handleCreateAndAssign} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    className="input input-sm w-full"
                    placeholder="First name"
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    className="input input-sm w-full"
                    placeholder="Last name"
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                  />
                  <input
                    type="email"
                    className="input input-sm w-full"
                    placeholder="Email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!newFirstName.trim() || !newEmail.trim()}>
                    Create & Assign
                  </button>
                </div>
              </form>
            ) : (
              <button onClick={() => setShowAddForm(true)} className="btn btn-ghost btn-sm">
                <span className="iconify lucide--plus size-4" />
                Create new facilitator
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Session QR Code dialog */}
      <dialog ref={sessionDialogRef} className="modal" onClose={() => setSessionQrFacilitator(null)}>
        <div className="modal-box max-w-sm">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              <span className="iconify lucide--x size-4" />
            </button>
          </form>

          <h3 className="font-bold text-lg mb-1">Session QR Code</h3>
          {sessionQrFacilitator && (
            <p className="text-sm text-base-content/60 mb-4">
              For <strong>{sessionQrFacilitator.name}</strong>
            </p>
          )}

          <fieldset className="fieldset mb-4">
            <legend className="fieldset-legend">Session identifier</legend>
            <input
              type="text"
              className="input w-full"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="e.g. Session 3, Week 6, Day 1..."
              list="existing-sessions-show"
            />
            {survey.existing_sessions.length > 0 && (
              <datalist id="existing-sessions-show">
                {survey.existing_sessions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
            <p className="label text-xs whitespace-normal">Type a new session ID or select an existing one</p>
          </fieldset>

          {sessionId.trim() && sessionQrFacilitator && (() => {
            const sessionUrl = `${sessionQrFacilitator.survey_url}&s=${encodeURIComponent(sessionId.trim())}`
            const filename = `${survey.name} - ${sessionQrFacilitator.name} - ${sessionId.trim()}`.replace(/[^a-z0-9]/gi, '_').toLowerCase()
            return (
              <div className="border-t border-base-300 pt-4 text-center space-y-3">
                <QRCodeSVG value={sessionUrl} size={200} level="M" marginSize={2} />
                <p className="text-xs text-base-content/40 break-all">{sessionUrl}</p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const svg = sessionDialogRef.current?.querySelector('svg')
                    if (!svg) return
                    const canvas = document.createElement('canvas')
                    canvas.width = 1024; canvas.height = 1024
                    const ctx = canvas.getContext('2d')
                    if (!ctx) return
                    const svgData = new XMLSerializer().serializeToString(svg)
                    const img = new Image()
                    img.onload = () => {
                      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 1024, 1024)
                      ctx.drawImage(img, 0, 0, 1024, 1024)
                      const a = document.createElement('a')
                      a.download = `${filename}_qr.png`
                      a.href = canvas.toDataURL('image/png')
                      a.click()
                    }
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
                  }}
                >
                  <span className="iconify lucide--download size-4" />
                  Download PNG
                </button>
              </div>
            )
          })()}
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </>
  )
}
